// VoicePanel — plain Office UI shell that lazy-loads the ElevenLabs SDK only
// when the CEO toggles the mic ON. The @elevenlabs/react chunk is NEVER
// loaded on /dashboard or anywhere else, and NOT on /floor until this
// toggle fires (React.lazy + Suspense).
//
// A VoiceErrorBoundary wraps the lazy child so any runtime error inside
// the SDK (missing provider, mic-permission crash, WebRTC failure, etc.)
// shows a small "Voice unavailable" message in the panel instead of
// taking down the whole /floor page.
import React, { lazy, Suspense, useCallback, useState } from "react";
import { Panel, DsButton } from "../../ds";

const VoiceSession = lazy(() => import("./VoiceSession.jsx"));


class VoiceErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(err) {
    return { err };
  }
  componentDidCatch(err, info) {
    // Log only — never bubble up to the Floor page.
    // eslint-disable-next-line no-console
    console.error("Voice runtime error:", err, info?.componentStack);
    this.props.onError?.(err);
  }
  render() {
    if (this.state.err) {
      return (
        <div
          className="text-[11px] text-[color:var(--bad)]"
          data-testid="voice-error-boundary"
        >
          Voice unavailable. Toggle off and try again.
        </div>
      );
    }
    return this.props.children;
  }
}


export default function VoicePanel() {
  const [on, setOn] = useState(false);
  const [lastStatus, setLastStatus] = useState("Off");

  const toggle = useCallback(() => setOn((v) => !v), []);

  return (
    <Panel
      label="VOICE (BETA)"
      action={
        <span
          className="font-mono text-[10.5px] text-[color:var(--muted)]"
          data-testid="voice-state-badge"
        >
          {on ? lastStatus : "Off"}
        </span>
      }
      testId="voice-panel"
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <DsButton
            variant={on ? "primary" : "secondary"}
            onClick={toggle}
            data-testid="voice-mic-toggle"
            className="h-9 text-sm"
          >
            {on ? "Stop voice" : "Start voice (TR)"}
          </DsButton>
          <span className="text-[11px] text-[color:var(--muted)]">
            Chief of Staff · Turkish
          </span>
        </div>

        {on && (
          <VoiceErrorBoundary onError={() => setLastStatus("Error")}>
            <Suspense
              fallback={
                <div
                  className="text-xs text-[color:var(--muted)]"
                  data-testid="voice-loading"
                >
                  Loading voice SDK…
                </div>
              }
            >
              <VoiceSession
                onForcedOff={() => setOn(false)}
                onStatusChange={setLastStatus}
              />
            </Suspense>
          </VoiceErrorBoundary>
        )}

        {!on && (
          <div
            className="text-[11px] text-[color:var(--muted)]"
            data-testid="voice-off-hint"
          >
            Mic is off. No audio, no polling, no cost on this page.
          </div>
        )}
      </div>
    </Panel>
  );
}
